import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import PricingPage from './components/PricingPage';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import TwoFactorVerifyPage from './components/auth/TwoFactorVerifyPage';
import TwoFactorSetupPage from './components/auth/TwoFactorSetupPage';
import SignOutSuccessPage from './components/auth/SignOutSuccessPage';
import DashboardPage from './components/DashboardPage';
import BillingPage from './components/BillingPage';
import AdminPage from './components/AdminPage';
import ProtectedRoute from './components/ProtectedRoute';
import App from './App';
import { authService } from './services/authService';
import { subscriptionService } from './services/subscriptionService';
import { useSessionTimeout } from './hooks/useSessionTimeout';

const AppWithAuth: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());

  // Auto-logout after 30 minutes of inactivity
  useSessionTimeout(30 * 60 * 1000);

  useEffect(() => {
    console.log('AppWithAuth mounted. Initial auth state:', isAuthenticated);
    // Subscribe to auth changes
    const unsubscribe = authService.subscribe(() => {
      const isAuth = authService.isAuthenticated();
      const user = authService.getCurrentUser();

      console.log('AppWithAuth: Auth state changed to:', isAuth);
      setIsAuthenticated(isAuth);

      if (isAuth && user && user.id) {
        subscriptionService.logIn(user.id);
      } else if (!isAuth) {
        subscriptionService.logOut();
      }
    });

    return unsubscribe;
  }, []);

  return (
    <Router>
      <div className="App">
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />

        <Routes>
          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" replace /> :
                <LoginPage />
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" replace /> :
                <RegisterPage />
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" replace /> :
                <ForgotPasswordPage />
            }
          />
          <Route
            path="/2fa/verify"
            element={<TwoFactorVerifyPage />}
          />
          <Route
            path="/2fa/setup"
            element={
              <ProtectedRoute>
                <TwoFactorSetupPage />
              </ProtectedRoute>
            }
          />

          {/* Sign Out Success Route */}
          <Route
            path="/signout-success"
            element={<SignOutSuccessPage />}
          />

          {/* Protected Routes */}
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <PricingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Billing Route */}
          <Route
            path="/settings/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Route */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />

          {/* Main App Route */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <App />
              </ProtectedRoute>
            }
          />

          {/* Home Route - Login Page */}
          <Route
            path="/"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" replace /> :
                <LoginPage />
            }
          />

          {/* Catch all route */}
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />}
          />
        </Routes>
      </div>
    </Router>
  );
};

export default AppWithAuth;
